import { DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Clock, Calendar, Share, Send, Copy, ExternalLink, Check, Plus, Edit, Trash2 } from "lucide-react";
import ProjetoChecklistForm from "@/components/solar/ProjetoChecklistForm";
import { FluxoCard } from "@/components/solar/kanban/types";
import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { v4 as uuidv4 } from "uuid";
import { CustomItem } from "@/types";

interface ProjetoFormDialogProps {
  card: FluxoCard;
  onSave: (projetoData: any) => void;
  onCancel: () => void;
}

export const ProjetoFormDialog = ({
  card,
  onSave,
  onCancel
}: ProjetoFormDialogProps) => {
  const { toast } = useToast();
  const [cardData, setCardData] = useState<FluxoCard | null>(null);
  const [activeTab, setActiveTab] = useState("detalhes");
  const [formLink, setFormLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  
  const [customItems, setCustomItems] = useState<CustomItem[]>([]);
  const [savedItems, setSavedItems] = useState<CustomItem[]>([]);
  const [newItem, setNewItem] = useState<CustomItem>({
    id: uuidv4(),
    name: '',
    description: '',
    quantity: 1,
    unitPrice: 0,
    type: 'outro'
  });
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [itemToAdd, setItemToAdd] = useState<string>('');
  const [currentCategory, setCurrentCategory] = useState<'painel' | 'inversor' | 'outro'>('outro');
  const [itemQuantity, setItemQuantity] = useState<number>(1);

  useEffect(() => {
    const storedItems = localStorage.getItem('customProjectItems');
    if (storedItems) {
      try {
        setSavedItems(JSON.parse(storedItems));
      } catch (error) {
        console.error('Error loading saved items:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (card) {
      setCardData(JSON.parse(JSON.stringify(card)));
      
      if (card.projetoCompleto?.customItems) {
        setCustomItems(card.projetoCompleto.customItems);
      }
    }
  }, [card]);

  useEffect(() => {
    if (linkCopied) {
      const timer = setTimeout(() => setLinkCopied(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [linkCopied]);

  useEffect(() => {
    if (linkSent) {
      const timer = setTimeout(() => setLinkSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [linkSent]);

  const handleDoubleClick = () => {
    if (cardData?.projetoCompleto) {
      handleSaveProject();
      toast({
        title: "Projeto salvo",
        description: "Suas alterações foram salvas com sucesso através do duplo clique."
      });
    }
  };

  const handleSaveProject = () => {
    if (cardData?.projetoCompleto) {
      const updatedProjetoCompleto = {
        ...cardData.projetoCompleto,
        customItems: customItems
      };
      
      onSave(updatedProjetoCompleto);
    }
  };

  const handleAddCustomItem = () => {
    if (!newItem.name || newItem.unitPrice <= 0) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha o nome e preço unitário do item.",
        variant: "destructive"
      });
      return;
    }
    
    setCustomItems([...customItems, { ...newItem, id: uuidv4() }]);
    
    setNewItem({
      id: uuidv4(),
      name: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      type: currentCategory
    });
    
    toast({
      title: "Item adicionado",
      description: "O item foi adicionado ao projeto com sucesso."
    });
  };

  const handleUpdateCustomItem = () => {
    if (!editingItem) return;
    
    const updatedItems = customItems.map(item => 
      item.id === editingItem ? { ...newItem } : item
    );
    
    setCustomItems(updatedItems);
    setEditingItem(null);
    
    setNewItem({
      id: uuidv4(),
      name: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      type: currentCategory
    });
    
    toast({
      title: "Item atualizado",
      description: "O item foi atualizado com sucesso."
    });
  };

  const handleEditCustomItem = (itemId: string) => {
    const itemToEdit = customItems.find(item => item.id === itemId);
    if (itemToEdit) {
      setNewItem({ ...itemToEdit });
      setEditingItem(itemId);
      setCurrentCategory(itemToEdit.type);
    }
  };

  const handleDeleteCustomItem = (itemId: string) => {
    setCustomItems(customItems.filter(item => item.id !== itemId));
    
    toast({
      title: "Item excluído",
      description: "O item foi removido do projeto."
    });
  };

  const handleSaveItemToLibrary = (item: CustomItem) => {
    const updatedSavedItems = [...savedItems];
    const existingItemIndex = updatedSavedItems.findIndex(i => i.id === item.id);
    
    if (existingItemIndex >= 0) {
      updatedSavedItems[existingItemIndex] = item;
    } else {
      updatedSavedItems.push({ ...item, id: uuidv4() });
    }
    
    setSavedItems(updatedSavedItems);
    localStorage.setItem('customProjectItems', JSON.stringify(updatedSavedItems));
    
    toast({
      title: "Item salvo na biblioteca",
      description: "O item foi salvo para uso em projetos futuros."
    });
  };

  const handleAddSavedItem = () => {
    if (!itemToAdd) {
      toast({
        title: "Selecione um item",
        description: "Por favor, selecione um item da biblioteca para adicionar.",
        variant: "destructive"
      });
      return;
    }
    
    const selectedItem = savedItems.find(item => item.id === itemToAdd);
    if (selectedItem) {
      const newItemWithQuantity = {
        ...selectedItem,
        id: uuidv4(),
        quantity: itemQuantity
      };
      
      setCustomItems([...customItems, newItemWithQuantity]);
      setItemToAdd('');
      setItemQuantity(1);
      
      toast({
        title: "Item adicionado",
        description: "O item da biblioteca foi adicionado ao projeto."
      });
    }
  };

  const handleEnviarFormulario = () => {
    if (!cardData) return;
    
    const baseUrl = window.location.origin;
    const linkFormulario = `${baseUrl}/checklist`;
    setFormLink(linkFormulario);
    
    navigator.clipboard.writeText(linkFormulario)
      .then(() => {
        setLinkCopied(true);
        toast({
          title: "Link copiado!",
          description: "O link para preenchimento do projeto foi copiado para a área de transferência."
        });
      })
      .catch(() => {
        toast({
          title: "Não foi possível copiar",
          description: "Link para preenchimento: " + linkFormulario,
          variant: "destructive"
        });
      });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(formLink)
      .then(() => {
        setLinkCopied(true);
        toast({
          title: "Link copiado!",
          description: "Link copiado para a área de transferência."
        });
      })
      .catch(() => {
        toast({
          variant: "destructive",
          title: "Erro ao copiar",
          description: "Não foi possível copiar o link."
        });
      });
  };

  const handleSendEmail = () => {
    if (!cardData) return;
    
    const emailSubject = encodeURIComponent(`Preenchimento de Projeto Solar: ${cardData.titulo}`);
    const emailBody = encodeURIComponent(
      `Olá,\n\nSegue o link para preenchimento dos dados do projeto solar "${cardData.titulo}":\n\n${formLink}\n\nPor favor, preencha as informações solicitadas para darmos continuidade ao projeto.\n\nAtenciosamente,\nEquipe Solar`
    );
    
    window.open(`mailto:?subject=${emailSubject}&body=${emailBody}`, '_blank');
    setLinkSent(true);
    
    toast({
      title: "Email preparado",
      description: "Uma nova mensagem de email foi preparada com o link."
    });
  };

  const handleOpenLink = () => {
    window.open(formLink, '_blank', 'noopener,noreferrer');
  };

  if (!cardData) {
    return (
      <DialogContent className="max-w-screen-xl w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Carregando detalhes do projeto...
          </DialogTitle>
        </DialogHeader>
        <div className="py-8 flex justify-center">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-muted rounded"></div>
                <div className="h-4 bg-muted rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    );
  }

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityBadge = (prioridade: string) => {
    switch(prioridade) {
      case "alta": return "destructive";
      case "media": return "warning";
      case "baixa": return "success";
      default: return "secondary";
    }
  };

  const filteredSavedItems = savedItems.filter(item => item.type === currentCategory);

  return (
    <DialogContent 
      className="max-w-screen-xl w-full max-h-[90vh] overflow-y-auto"
      onDoubleClick={handleDoubleClick}
    >
      <DialogHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <DialogTitle className="flex items-center gap-2 text-xl font-bold">
              <FileText className="h-5 w-5" />
              {cardData.titulo}
            </DialogTitle>
            <DialogDescription className="mt-2 flex flex-wrap gap-3 items-center">
              <span className="flex items-center gap-1 text-sm bg-muted/50 px-2 py-1 rounded-md">
                <Calendar className="h-4 w-4" />
                {formatDate(cardData.criadoEm)}
              </span>
              
              <Badge variant={getPriorityBadge(cardData.prioridade)} className="text-xs">
                Prioridade {cardData.prioridade === "alta" ? "Alta" : cardData.prioridade === "media" ? "Média" : "Baixa"}
              </Badge>
              
              <Badge variant="outline" className="text-xs">
                {cardData.status === "lead" ? "Lead" : 
                 cardData.status === "contato" ? "Em Contato" : 
                 cardData.status === "proposta" ? "Proposta Enviada" : 
                 cardData.status === "negociacao" ? "Em Negociação" : 
                 cardData.status === "fechado" ? "Fechado" : "Perdido"}
              </Badge>
            </DialogDescription>
          </div>
          
          <Button 
            onClick={handleEnviarFormulario}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <Send className="h-4 w-4" />
            {linkCopied ? "Link Copiado!" : "Gerar Link"}
          </Button>
        </div>
      </DialogHeader>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mt-4">
        <TabsList className="mb-4 w-full">
          <TabsTrigger value="detalhes" className="flex-1">Detalhes do Projeto</TabsTrigger>
          <TabsTrigger value="itens" className="flex-1">Itens Personalizados</TabsTrigger>
        </TabsList>
        
        <TabsContent value="detalhes" className="rounded-md border p-4 bg-card/50 shadow-sm">
          <div className="mb-2 text-sm text-muted-foreground text-center italic">
            Dica: Dê um duplo clique em qualquer lugar do formulário para salvar rapidamente
          </div>
          <ProjetoChecklistForm 
            projeto={cardData.projetoCompleto} 
            onSave={onSave}
            onCancel={onCancel}
          />
        </TabsContent>
        
        <TabsContent value="itens" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {editingItem ? "Editar Item" : "Adicionar Novo Item"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="itemType">Categoria</Label>
                  <Select 
                    value={currentCategory} 
                    onValueChange={(value: 'painel' | 'inversor' | 'outro') => {
                      setCurrentCategory(value);
                      setNewItem(prev => ({ ...prev, type: value }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="painel">Painéis</SelectItem>
                      <SelectItem value="inversor">Inversores</SelectItem>
                      <SelectItem value="outro">Outros Itens</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="itemName">Nome do Item*</Label>
                  <Input 
                    id="itemName" 
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    placeholder="Ex: Cabo Solar 6mm²"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="itemDescription">Descrição</Label>
                  <Input 
                    id="itemDescription" 
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Ex: Cabo flexível para sistemas fotovoltaicos"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="itemQuantity">Quantidade*</Label>
                    <Input 
                      id="itemQuantity" 
                      type="number"
                      min="1"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="itemPrice">Preço Unitário (R$)*</Label>
                    <Input 
                      id="itemPrice" 
                      type="number"
                      step="0.01"
                      min="0"
                      value={newItem.unitPrice}
                      onChange={(e) => setNewItem({ ...newItem, unitPrice: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  {editingItem ? (
                    <>
                      <Button 
                        type="button" 
                        className="flex-1"
                        onClick={handleUpdateCustomItem}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Atualizar Item
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => {
                          setEditingItem(null);
                          setNewItem({
                            id: uuidv4(),
                            name: '',
                            description: '',
                            quantity: 1,
                            unitPrice: 0,
                            type: currentCategory
                          });
                        }}
                      >
                        Cancelar
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        type="button" 
                        className="flex-1"
                        onClick={handleAddCustomItem}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Adicionar Item
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => handleSaveItemToLibrary(newItem)}
                        disabled={!newItem.name || newItem.unitPrice <= 0}
                      >
                        Salvar na Biblioteca
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Adicionar da Biblioteca</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="savedItemCategory">Categoria</Label>
                  <Select 
                    value={currentCategory} 
                    onValueChange={(value: 'painel' | 'inversor' | 'outro') => setCurrentCategory(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="painel">Painéis</SelectItem>
                      <SelectItem value="inversor">Inversores</SelectItem>
                      <SelectItem value="outro">Outros Itens</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="savedItemSelect">Item Salvo</Label>
                  <Select 
                    value={itemToAdd} 
                    onValueChange={setItemToAdd}
                    disabled={filteredSavedItems.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={
                        filteredSavedItems.length === 0 
                          ? "Nenhum item salvo nesta categoria" 
                          : "Selecione um item salvo"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredSavedItems.map(item => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name} - R$ {item.unitPrice.toFixed(2)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="savedItemQuantity">Quantidade</Label>
                  <Input 
                    id="savedItemQuantity" 
                    type="number"
                    min="1"
                    value={itemQuantity}
                    onChange={(e) => setItemQuantity(parseInt(e.target.value) || 1)}
                  />
                </div>
                
                <Button 
                  type="button" 
                  onClick={handleAddSavedItem}
                  disabled={!itemToAdd}
                  className="w-full mt-4"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Adicionar ao Projeto
                </Button>
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Itens Adicionados ao Projeto</CardTitle>
            </CardHeader>
            <CardContent>
              {customItems.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  Nenhum item personalizado adicionado. Adicione itens usando o formulário acima.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-2 text-left">Categoria</th>
                        <th className="px-4 py-2 text-left">Nome</th>
                        <th className="px-4 py-2 text-left">Descrição</th>
                        <th className="px-4 py-2 text-right">Quantidade</th>
                        <th className="px-4 py-2 text-right">Preço Unit.</th>
                        <th className="px-4 py-2 text-right">Total</th>
                        <th className="px-4 py-2 text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customItems.map(item => (
                        <tr key={item.id} className="border-b hover:bg-muted/50">
                          <td className="px-4 py-2">
                            {item.type === 'painel' ? 'Painel' : 
                             item.type === 'inversor' ? 'Inversor' : 'Outro'}
                          </td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-muted-foreground text-sm">{item.description}</td>
                          <td className="px-4 py-2 text-right">{item.quantity}</td>
                          <td className="px-4 py-2 text-right">
                            {item.unitPrice.toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL'
                            })}
                          </td>
                          <td className="px-4 py-2 text-right font-medium">
                            {(item.quantity * item.unitPrice).toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL'
                            })}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <div className="flex justify-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditCustomItem(item.id)}
                                className="h-8 w-8"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleSaveItemToLibrary(item)}
                                className="h-8 w-8 text-blue-500"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteCustomItem(item.id)}
                                className="h-8 w-8 text-red-500"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted/30">
                        <td colSpan={5} className="px-4 py-2 text-right font-semibold">
                          Total:
                        </td>
                        <td className="px-4 py-2 text-right font-bold">
                          {customItems.reduce((total, item) => total + (item.quantity * item.unitPrice), 0)
                            .toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL'
                            })}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              
              <div className="mt-4 flex justify-end">
                <Button
                  type="button"
                  onClick={handleSaveProject}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Salvar Todas as Alterações
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {formLink && (
        <div className="mt-4 p-4 border rounded-md bg-card shadow-sm">
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Share className="h-4 w-4 text-primary" />
            Link para preenchimento externo
          </h3>
          
          <div className="relative">
            <Input 
              value={formLink} 
              readOnly 
              className="pr-24 font-mono text-xs bg-muted/20"
            />
            <div className="absolute right-1 top-1 flex gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                onClick={handleCopyLink}
                title="Copiar link"
              >
                {linkCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                onClick={handleOpenLink}
                title="Abrir link"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          
          <div className="mt-3 flex justify-end gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-1.5"
              onClick={handleCopyLink}
            >
              {linkCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {linkCopied ? "Copiado!" : "Copiar"}
            </Button>
            <Button 
              size="sm" 
              className="flex items-center gap-1.5"
              onClick={handleSendEmail}
            >
              <Send className="h-3.5 w-3.5" />
              {linkSent ? "Email Preparado" : "Enviar por Email"}
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  );
};
